import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SearchResult } from '../lib/search-types';
import {
  mapDocumentHits,
  mapEventHits,
  mapOrgHits,
  mapPeopleHits,
  type RawHit,
} from '../lib/map-search-hits';
import {
  typesenseClient,
  EVENTS_COLLECTION,
  PEOPLE_COLLECTION,
  DOCUMENTS_COLLECTION,
  ORGANIZATIONS_COLLECTION,
} from '../lib/typesense';

const DEBOUNCE_MS = 150;

function useDebounced(value: string): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value]);
  return debounced;
}

type SearchSpec = {
  collection: string;
  q: string;
  query_by: string;
  per_page: number;
  filter_by?: string;
  highlight_full_fields?: string;
  highlight_affix_num_tokens?: number;
  exhaustive_search?: boolean;
};

type MultiSearchResult = {
  hits?: RawHit[];
  code?: number;
  error?: string;
};

function buildSearches(q: string, caseId: string | null): SearchSpec[] {
  const filterBy = caseId ? `caseId:=${caseId}` : undefined;
  const base = {
    q,
    ...(filterBy ? { filter_by: filterBy } : {}),
    highlight_affix_num_tokens: 14,
    exhaustive_search: true,
  };
  return [
    {
      ...base,
      collection: EVENTS_COLLECTION,
      query_by:
        'title,contentPreview,fromEmail,participantEmails,participantHandles,attachmentNames',
      highlight_full_fields: 'title,contentPreview,attachmentNames,fromEmail',
      per_page: 8,
    },
    {
      ...base,
      collection: PEOPLE_COLLECTION,
      query_by: 'navn,normalizedName,aliases,emails,phones',
      highlight_full_fields: 'navn,aliases,emails,phones',
      per_page: 4,
    },
    {
      ...base,
      collection: DOCUMENTS_COLLECTION,
      query_by: 'filnavn,textContent',
      highlight_full_fields: 'filnavn,textContent',
      per_page: 5,
    },
    {
      ...base,
      collection: ORGANIZATIONS_COLLECTION,
      query_by: 'navn,aliases,domains,orgNumber',
      highlight_full_fields: 'navn,aliases,domains,orgNumber',
      per_page: 3,
    },
  ];
}

function hitsOf(result: MultiSearchResult | undefined): RawHit[] {
  if (!result || result.error || typeof result.code === 'number') return [];
  return result.hits ?? [];
}

/**
 * Kjører multi_search mot Typesense.
 * Scopes med filter_by caseId når brukeren er i et saksrom.
 */
async function runMultiSearch(q: string, caseId: string | null): Promise<SearchResult[]> {
  if (!typesenseClient || !q.trim()) return [];
  const raw = await typesenseClient.multiSearch.perform({ searches: buildSearches(q, caseId) });
  const response = raw as { results?: MultiSearchResult[] };
  const [eventsRes, peopleRes, docsRes, orgsRes] = response.results ?? [];
  return [
    ...mapEventHits(hitsOf(eventsRes)),
    ...mapPeopleHits(hitsOf(peopleRes)),
    ...mapDocumentHits(hitsOf(docsRes)),
    ...mapOrgHits(hitsOf(orgsRes)),
  ];
}

export interface SearchState {
  results: SearchResult[];
  isSearching: boolean;
  isConfigured: boolean;
}

export function useSearch(rawQuery: string, caseId: string | null): SearchState {
  const debouncedQuery = useDebounced(rawQuery);
  const isConfigured = Boolean(typesenseClient);

  const { data, isFetching } = useQuery({
    queryKey: ['search', debouncedQuery, caseId] as const,
    queryFn: () => runMultiSearch(debouncedQuery, caseId),
    enabled: isConfigured && Boolean(debouncedQuery.trim()),
    staleTime: 30_000,
  });

  return { results: data ?? [], isSearching: isFetching, isConfigured };
}
